import React, { useEffect } from 'react';

import { useAccountState, useAccountActions } from './AccountContext';
import { getSubscription, createSubscription, cancelSubscription } from './restApi';
import PageSectionTitle from '../../Shared/UI/PageSectionTitle/components/PageSectionTitle';
import Button from '../../Shared/UI/Button/components/Button';

const AccountSubscription = () => {
  const state = useAccountState();
  const { onResponse, onError, setSubscriptionProcessing, setUsageType } = useAccountActions();

  useEffect(() => {
    getSubscription({ onResponse, onError });
  }, []);

  const handleSubscribe = () => {
    const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
    setSubscriptionProcessing(true);
    createSubscription({
      onResponse: (data) => {
        setSubscriptionProcessing(false);
        onResponse(data);
      },
      onError: (data) => {
        setSubscriptionProcessing(false);
        onError(data);
      },
      body: { authenticityToken, usageType },
    });
  };

  const handleCancel = () => {
    const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
    setSubscriptionProcessing(true);
    cancelSubscription({
      onResponse: (data) => {
        setSubscriptionProcessing(false);
        onResponse(data);
      },
      onError: (data) => {
        setSubscriptionProcessing(false);
        onError(data);
      },
      body: { authenticityToken },
    });
  };

  const subscription = state?.subscription;
  const hasPaymentMethod = !!state?.paymentMethod;
  const processing = state?.subscriptionProcessing;
  const usageType = state?.usageType || 'personal';

  if (state?.loading) {
    return (
      <>
        <PageSectionTitle text="Job Recommendations Subscription" />
        <p>Loading...</p>
      </>
    );
  }

  if (subscription && subscription.feeWaived) {
    return (
      <>
        <PageSectionTitle text="Job Recommendations Subscription" />
        <p>
          <strong>Premium Job Recommendations</strong> -- Fee Waived
        </p>
        <p>Status: Active</p>
        <p>AI-powered Daily job recommendations matching for your profile.</p>
      </>
    );
  }

  if (subscription && subscription.status === 'active' && subscription.canceledAt) {
    const endDate = new Date(subscription.currentPeriodEnd).toLocaleDateString();
    return (
      <>
        <PageSectionTitle text="Job Recommendations Subscription" />
        <p>Your subscription has been canceled and will remain active until {endDate}.</p>
        <p>After this date, your job recommendations will switch to the free tier (once per month).</p>
      </>
    );
  }

  if (subscription && (subscription.status === 'active' || subscription.status === 'past_due')) {
    const nextBilling = new Date(subscription.currentPeriodEnd).toLocaleDateString();
    return (
      <>
        <PageSectionTitle text="Job Recommendations Subscription" />
        <p>
          <strong>Premium Job Recommendations</strong> -- $19.99/month + applicable tax
        </p>
        <p>Status: {subscription.status === 'active' ? 'Active' : 'Past due'}</p>
        <p>Next billing date: {nextBilling}</p>
        <p>Daily AI-powered job matching for your profile.</p>
        <Button
          variant="ghost"
          onClick={handleCancel}
          disabled={processing}
          style={{ marginTop: '8px' }}
        >
          {processing ? 'Canceling...' : 'Cancel Subscription'}
        </Button>
      </>
    );
  }

  return (
    <>
      <PageSectionTitle text="Job Recommendations Subscription" />
      <div style={{
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '16px',
      }}>
        <p style={{ fontWeight: 600, marginBottom: '8px' }}>
          Premium Job Recommendations -- $19.99/month + applicable tax
        </p>
        <ul style={{ marginBottom: '16px', paddingLeft: '20px' }}>
          <li>Daily AI-powered job matching tailored to your skills and experience</li>
          <li>30 AI-generated resumes per month, customized to each job</li>
        </ul>
        <p style={{ color: '#666', marginBottom: '16px' }}>
          Free tier: job recommendations once per month (no payment required).
        </p>
        <div style={{ marginBottom: '12px' }}>
          <p style={{ fontWeight: 500, marginBottom: '8px' }}>
            How will you use Premium Job Recommendations?
          </p>
          <label style={{ display: 'block', marginBottom: '4px' }}>
            <input
              type="radio"
              name="usageType"
              value="personal"
              checked={usageType === 'personal'}
              onChange={() => setUsageType('personal')}
            />
            {' '}Personal use
          </label>
          <label style={{ display: 'block' }}>
            <input
              type="radio"
              name="usageType"
              value="business"
              checked={usageType === 'business'}
              onChange={() => setUsageType('business')}
            />
            {' '}Business use
          </label>
        </div>
        <Button
          variant="primary"
          onClick={handleSubscribe}
          disabled={processing || !hasPaymentMethod}
        >
          {processing ? 'Subscribing...' : 'Subscribe'}
        </Button>
        {!hasPaymentMethod && (
          <p style={{ color: '#666', marginTop: '8px', fontSize: '14px' }}>
            Please add a payment method above before subscribing.
          </p>
        )}
      </div>
    </>
  );
};

export default AccountSubscription;
